export type Language = 'es' | 'en'

const es = {
  nav: {
    system: 'Sistema',
    solution: 'Solución',
    team: 'Equipo',
    services: 'Otros servicios',
    cta: 'Coordinemos una reunión',
    openMenu: 'Abrir menú',
    closeMenu: 'Cerrar menú',
    language: 'Idioma',
  },
  hero: {
    title: 'Todo bajo control.',
    subtitle:
      'Estrategia, equipo y tecnología para gestionar tu presencia digital.',
    cta: 'Conocé Profumind',
    videoLabel: 'Recorrido por la plataforma Profumind.app',
  },
  clarity: {
    title: 'Claridad para avanzar.',
    text: 'Visualizá qué se está haciendo. Qué sigue. Y qué resultados está generando.',
  },
  system: {
    title: 'Un sistema propio.',
    text: 'Cada proyecto. Cada etapa. Cada resultado. En un mismo lugar.',
    cta: 'Explorar el sistema',
  },
  insights: {
    title: 'Información que se transforma en decisiones.',
    text: 'Métricas e informes para entender qué sucede y definir qué sigue.',
  },
  team: {
    title: 'Un equipo detrás de tu negocio.',
    roles: 'Estrategia. Dirección creativa. Diseño. Edición. Análisis.',
    anywhere: 'Desde cualquier lugar.',
    imageAlt:
      'Integrantes del equipo Profumind trabajando de forma remota, cada uno desde su propio espacio',
  },
  structure: {
    title: 'Una estructura completa.',
    items: [
      { title: 'ESTRATEGIA', word: 'Dirección.' },
      { title: 'CONTENIDO', word: 'Ejecución.' },
      { title: 'SISTEMA', word: 'Control.' },
      { title: 'ANÁLISIS', word: 'Evolución.' },
    ],
  },
  services: {
    title: 'Y cuando tu negocio necesita más.',
    items: [
      {
        title: 'Sitios web',
        desc: 'SEO · IA · Social Preview · Identidad digital',
      },
      {
        title: 'Otros desarrollos',
        desc: 'Soluciones digitales adaptadas al proyecto.',
      },
    ],
  },
  process: {
    title: 'La constancia no debería depender de una persona.',
    stages: [
      'PLANIFICADO',
      'EN PRODUCCIÓN',
      'APROBADO',
      'PUBLICADO',
      'ANALIZADO',
    ],
    card: 'Reel · Lanzamiento de temporada',
    updated: 'Actualizado ahora',
  },
  closing: {
    title: 'Tu negocio. En movimiento.',
    text: 'Una estructura diseñada para que las cosas sucedan.',
    cta: 'Coordinemos una reunión',
  },
  footer: {
    rights: '© 2026 Profumind. Todos los derechos reservados.',
  },
  ui: {
    sidebar: ['Resumen', 'Planificación', 'Contenidos', 'Calendario', 'Informes'],
    planner: {
      title: 'Planificación',
      week: 'Semana 39 · Septiembre',
      synced: 'Sincronizado',
      columns: [
        {
          name: 'Por hacer',
          tasks: [
            { title: 'Guion reel de lanzamiento', tag: 'Contenido', date: '26 sep', tone: 0 },
            { title: 'Brief campaña primavera', tag: 'Estrategia', date: '27 sep', tone: 1 },
          ],
        },
        {
          name: 'En curso',
          tasks: [
            { title: 'Edición video testimonio', tag: 'Edición', date: 'Hoy', tone: 2 },
            { title: 'Carrusel de servicios', tag: 'Diseño', date: '25 sep', tone: 3 },
          ],
        },
        {
          name: 'Revisión',
          tasks: [
            { title: 'Stories semana 40', tag: 'Diseño', date: '24 sep', tone: 3 },
          ],
        },
        {
          name: 'Listo',
          tasks: [
            { title: 'Informe de agosto', tag: 'Análisis', date: '22 sep', tone: 0 },
            { title: 'Calendario de octubre', tag: 'Estrategia', date: '21 sep', tone: 1 },
          ],
        },
      ],
    },
    system: {
      title: 'Proyectos',
      active: '4 activos',
      stages: ['Estrategia', 'Producción', 'Aprobación', 'Publicación', 'Análisis'],
      stats: [
        { label: 'Entregas esta semana', value: '24' },
        { label: 'En aprobación', value: '6' },
        { label: 'Publicado a tiempo', value: '98%' },
      ],
    },
    analytics: {
      title: 'Informe · Septiembre',
      range: 'Últimos 30 días',
      kpis: [
        { label: 'Alcance', value: '284K', delta: '+18%' },
        { label: 'Interacción', value: '6,2%', delta: '+1,4' },
        { label: 'Consultas', value: '142', delta: '+32%' },
        { label: 'Conversión', value: '4,8%', delta: '+0,9' },
      ],
      trend: 'Consultas generadas',
      channels: 'Rendimiento por canal',
      next: 'Próximos pasos',
      actions: [
        'Duplicar formato reel testimonio',
        'Reforzar tráfico al sitio web',
        'Ajustar horarios de publicación',
      ],
    },
  },
}

export type Dictionary = typeof es

const en: Dictionary = {
  nav: {
    system: 'System',
    solution: 'Solution',
    team: 'Team',
    services: 'More services',
    cta: 'Schedule a meeting',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    language: 'Language',
  },
  hero: {
    title: 'Everything under control.',
    subtitle:
      'Strategy, people and technology to run your digital presence.',
    cta: 'Discover Profumind',
    videoLabel: 'A walkthrough of the Profumind.app platform',
  },
  clarity: {
    title: 'Clarity to move forward.',
    text: "See what's being done. What's next. And what it's delivering.",
  },
  system: {
    title: 'A system of our own.',
    text: 'Every project. Every stage. Every result. In one place.',
    cta: 'Explore the system',
  },
  insights: {
    title: 'Data that turns into decisions.',
    text: "Metrics and reports to understand what's happening and decide what comes next.",
  },
  team: {
    title: 'A team behind your business.',
    roles: 'Strategy. Creative direction. Design. Editing. Analytics.',
    anywhere: 'From anywhere.',
    imageAlt:
      'Profumind team members working remotely, each from their own workspace',
  },
  structure: {
    title: 'A complete structure.',
    items: [
      { title: 'STRATEGY', word: 'Direction.' },
      { title: 'CONTENT', word: 'Execution.' },
      { title: 'SYSTEM', word: 'Control.' },
      { title: 'ANALYTICS', word: 'Evolution.' },
    ],
  },
  services: {
    title: 'And when your business needs more.',
    items: [
      {
        title: 'Websites',
        desc: 'SEO · AI · Social Preview · Digital identity',
      },
      {
        title: 'Custom development',
        desc: 'Digital solutions tailored to each project.',
      },
    ],
  },
  process: {
    title: "Consistency shouldn't depend on one person.",
    stages: ['PLANNED', 'IN PRODUCTION', 'APPROVED', 'PUBLISHED', 'ANALYZED'],
    card: 'Reel · Season launch',
    updated: 'Updated just now',
  },
  closing: {
    title: 'Your business. In motion.',
    text: 'A structure designed to make things happen.',
    cta: 'Schedule a meeting',
  },
  footer: {
    rights: '© 2026 Profumind. All rights reserved.',
  },
  ui: {
    sidebar: ['Overview', 'Planning', 'Content', 'Calendar', 'Reports'],
    planner: {
      title: 'Planning',
      week: 'Week 39 · September',
      synced: 'Synced',
      columns: [
        {
          name: 'To do',
          tasks: [
            { title: 'Launch reel script', tag: 'Content', date: 'Sep 26', tone: 0 },
            { title: 'Spring campaign brief', tag: 'Strategy', date: 'Sep 27', tone: 1 },
          ],
        },
        {
          name: 'In progress',
          tasks: [
            { title: 'Testimonial video edit', tag: 'Editing', date: 'Today', tone: 2 },
            { title: 'Services carousel', tag: 'Design', date: 'Sep 25', tone: 3 },
          ],
        },
        {
          name: 'Review',
          tasks: [
            { title: 'Week 40 stories', tag: 'Design', date: 'Sep 24', tone: 3 },
          ],
        },
        {
          name: 'Done',
          tasks: [
            { title: 'August report', tag: 'Analytics', date: 'Sep 22', tone: 0 },
            { title: 'October calendar', tag: 'Strategy', date: 'Sep 21', tone: 1 },
          ],
        },
      ],
    },
    system: {
      title: 'Projects',
      active: '4 active',
      stages: ['Strategy', 'Production', 'Approval', 'Publishing', 'Analytics'],
      stats: [
        { label: 'Deliverables this week', value: '24' },
        { label: 'Awaiting approval', value: '6' },
        { label: 'Published on time', value: '98%' },
      ],
    },
    analytics: {
      title: 'Report · September',
      range: 'Last 30 days',
      kpis: [
        { label: 'Reach', value: '284K', delta: '+18%' },
        { label: 'Engagement', value: '6.2%', delta: '+1.4' },
        { label: 'Leads', value: '142', delta: '+32%' },
        { label: 'Conversion', value: '4.8%', delta: '+0.9' },
      ],
      trend: 'Leads generated',
      channels: 'Performance by channel',
      next: 'Next steps',
      actions: [
        'Double down on testimonial reels',
        'Drive more traffic to the website',
        'Adjust posting schedule',
      ],
    },
  },
}

export const translations: Record<Language, Dictionary> = { es, en }
